import { createElement, type ComponentType, type ReactElement, type SVGProps } from 'react';
import { resolveMarketingMessageIcon, resolveMarketingMessageIconKey } from './marketingMessageIconRegistry';
import { MarketingWidgetTemplateProps } from './MarketingWidgetTemplate';
import type { Artifact } from '@/enterprise/data/useArtifacts';

/** Side-card icon sizing + white theming (parity with CMT `MARKETING_MESSAGE_SIDE_CARD_ICON_CLASSNAME`). */
export const LIQUIDITY_ICON_CLASSNAME =
  'size-24 p-2 text-white [&_path]:fill-white [&_path]:[fill-opacity:1] [&_path]:stroke-white';

type LiquidityIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Renders an icon from a Liquidity React component — used by client widgets and artifact path. */
export function renderLiquidityIconComponent(Icon: LiquidityIconComponent, alt: string): ReactElement {
  return createElement(Icon, {
    role: 'img',
    'aria-label': alt,
    className: LIQUIDITY_ICON_CLASSNAME,
  });
}

type MarketingMessageContent = Extract<NonNullable<Artifact['content']>, { title: string }>;

/** Icon for campaign artifacts: `content.icon.key` or default Liquidity fallback. */
export function renderArtifactIcon(content: MarketingMessageContent): ReactElement {
  const iconKey = content.icon?.key?.trim() || undefined;
  const resolvedKey = resolveMarketingMessageIconKey(iconKey);
  const Icon = resolveMarketingMessageIcon(resolvedKey);

  return renderLiquidityIconComponent(Icon, resolvedKey);
}

type MarketingMessageCta = MarketingMessageContent['primaryCta'];

interface CtaButton {
  buttonText: string;
  buttonLink: string;
  isExternalLink: boolean;
}

function mapCtaToButton(cta: MarketingMessageCta | undefined): CtaButton | undefined {
  // Dismiss not used yet, text and target (URL path) are required
  if (!cta || cta.action === 'DISMISS' || !cta.text || !cta.target) {
    return undefined;
  }

  return {
    buttonText: cta.text,
    buttonLink: cta.target,
    isExternalLink: cta.action === 'EXTERNAL_LINK',
  };
}

export function mapMarketingArtifactToWidgetProps(artifact: Artifact): MarketingWidgetTemplateProps {
  const content = artifact.content;
  if (!content || !('title' in content)) {
    throw new Error('mapMarketingArtifactToWidgetProps requires MARKETING_MESSAGE content');
  }

  const primaryCta = mapCtaToButton(content.primaryCta);
  const secondaryCta = mapCtaToButton(content.secondaryCta);
  const footerLogo = content.footer?.logo;

  return {
    icon: renderArtifactIcon(content),
    title: content.title,
    description: content.body,
    buttonText: primaryCta?.buttonText,
    buttonLink: primaryCta?.buttonLink,
    isExternalLink: primaryCta?.isExternalLink,
    secondaryButtonText: secondaryCta?.buttonText,
    secondaryButtonLink: secondaryCta?.buttonLink,
    secondaryIsExternalLink: secondaryCta?.isExternalLink,
    ...(content.footer
      ? {
          footer: {
            text: content.footer.text,
            logoIconKey: footerLogo && 'iconKey' in footerLogo ? footerLogo.iconKey : undefined,
            logoAlt: content.footer.logo.alt,
          },
        }
      : {}),
    trackingMetadata: {
      location: 'homepage',
      source: 'artifact',
      artifactUuid: artifact.tracking.artifactUuid,
      assetUuid: artifact.tracking.assetUuid,
      campaignUuid: artifact.tracking.campaignUuid,
      campaignRunUuid: artifact.tracking.campaignRunUuid,
    },
  };
}
